import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    DialogActions,
    Typography,
    DialogContent,
    DialogTitle,
    Container,
    Dialog,
    TextField,
    Paper,
    Pagination,
    PaginationItem,
    InputLabel,
    MenuItem,
    Select,
    InputAdornment,
    useMediaQuery,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useCookies } from "react-cookie";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import IconSidenav from "../../common/IconSidenav";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/Search";

const FilmList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cookies] = useCookies();

    const [page, setPage] = useState(0);
    const isMobileScreen = useMediaQuery(
        "(min-width:360px) and (max-width:500px)"
    );
    const isSmallScreen = useMediaQuery(
        "(min-width:1024px) and (max-width:1440px)"
    );
    const [rowsPerPage, setRowsPerPage] = useState(
        isMobileScreen ? 5 : isSmallScreen ? 5 : 10
    );
    const [rows, setRows] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [loading, setLoading] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);

    const columns = [
        { id: "item1", label: "Sl. No" },
        { id: "item2", label: "Poster" },
        { id: "item3", label: "Title" },
        { id: "item6", label: "Action" },
    ];
    const [showSideNav, setShowSideNav] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value.trim());
    };

    const filteredRows = (rows || []).filter((row) => {
        const { title } = row || {};
        const lowerCaseQuery = searchQuery.toLowerCase();
        return title && title.toLowerCase().includes(lowerCaseQuery);
    });

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const fetchTableData = async () => {
        try {
            setLoading(true);
            const response = await invokeApi(
                config.getMyCollege + apiList.getFilms,
                null,
                "POST",
                {},
                { "Content-Type": "application/json" }
            );

            if (response?.status === 200) {
                const responseData = response.data;
                let allFilms = [];
                if (Array.isArray(responseData)) {
                    allFilms = responseData;
                } else if (responseData?.data && Array.isArray(responseData.data)) {
                    allFilms = responseData.data;
                } else if (responseData?.films && Array.isArray(responseData.films)) {
                    allFilms = responseData.films;
                } else if (responseData?.items && Array.isArray(responseData.items)) {
                    allFilms = responseData.items;
                }
                setRows(allFilms);
            } else {
                console.error("Failed to fetch data:", response);
                setRows([]);
                showSnackbar("Failed to fetch films", "error");
            }
        } catch (error) {
            console.error("Error during data fetch:", error);
            setRows([]);
            showSnackbar("Error fetching films", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTableData();
    }, []);

    const handleEdit = async (id) => {
        const params = { id: id };
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.getFilm,
                params,
                cookies
            );

            if (response?.status === 200) {
                const filmData = response.data.film || response.data.data;
                navigate(`/films-manager/edit-film/${id}`, {
                    state: { filmData },
                });
            } else {
                showSnackbar("Something went wrong. Please try again later!!", "error");
            }
        } catch (error) {
            console.error("Error during data fetch:", error);
            showSnackbar("Something went wrong. Please try again later!!", "error");
        }
    };

    const handleFilmDelete = async () => {
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.deleteFilm,
                { id: deleteItemId },
                cookies
            );
            if (response?.status >= 200 && response?.status < 300) {
                showSnackbar("Film deleted successfully", "success");
                setOpenDeleteDialog(false);
                setRows((prevRows) => prevRows.filter((row) => row._id !== deleteItemId && row.id !== deleteItemId));
            } else {
                showSnackbar("Failed to delete. Please try again later!!", "error");
            }
        } catch (error) {
            showSnackbar("Failed to delete. Please try again later!!", "error");
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleOpenDeleteDialog = (id) => {
        setOpenDeleteDialog(true);
        setDeleteItemId(id);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
    const currentPage = Math.min(page, totalPages - 1);
    const paginatedRows = filteredRows.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    return (
        <>
            <Grid sx={{ display: "flex" }}>
                {isMobileScreen ? (
                    <IconSidenav />
                ) : showSideNav ? (
                    <IconSidenav />
                ) : (
                    <Sidenav />
                )}
                <Grid component="main" sx={{ width: "100%", px: 1 }}>
                    <Header toggleSideNav={toggleSideNav} />
                    <Grid sx={{ py: 1 }}>
                        <NavigatedComponent pathname={location.pathname} />
                        <Grid
                            sx={{
                                width: "100%",
                                px: isMobileScreen ? 1 : isSmallScreen ? 2 : 5,
                            }}
                        >
                            <Grid
                                container
                                md={12}
                                xs={12}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    my: 2,
                                }}
                            >
                                <Grid item md={12} xs={12}>
                                    <TextField
                                        type="text"
                                        size="small"
                                        placeholder="search"
                                        name="searchQuery"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: "#1D1A57" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            width: isMobileScreen ? "250px" : "400px",
                                            background: "#f0f8ff",
                                            borderRadius: "20px",
                                            border: "1px solid #1D1A57",
                                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                sx={{
                                    my: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >

                                {loading ? (
                                    <Box sx={{ display: "flex", height: "200px", alignItems: "center" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Paper sx={{ width: "100%", mb: 2 }}>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map((column) => (
                                                            <TableCell key={column.id} sx={{ fontWeight: "bold" }}>
                                                                {column.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedRows.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={columns.length} align="center">
                                                                No films found
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        paginatedRows.map((row, index) => (
                                                            <TableRow key={row._id || row.id || index}>
                                                                <TableCell>
                                                                    {index + 1 + currentPage * rowsPerPage}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <img
                                                                        src={row.imageUrl || row.image || "/default-film.jpg"}
                                                                        alt={row.title}
                                                                        style={{ width: 50, height: 50, objectFit: "cover" }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{row.title || row.name}</TableCell>
                                                                <TableCell>
                                                                    <Button onClick={() => handleEdit(row._id || row.id)}>
                                                                        <EditIcon color="primary" />
                                                                    </Button>
                                                                    <Button onClick={() => handleOpenDeleteDialog(row._id || row.id)}>
                                                                        <DeleteIcon color="error" />
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage + 1}
                                            onChange={(e, p) => handleChangePage(e, p - 1)}
                                            sx={{ p: 2, display: "flex", justifyContent: "center" }}
                                        />
                                    </Paper>
                                )}

                                {/* Delete Dialog */}
                                <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                                    <DialogTitle>Confirm Delete</DialogTitle>
                                    <DialogContent>
                                        <Typography>Are you sure you want to delete this film?</Typography>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseDeleteDialog}>No</Button>
                                        <Button onClick={handleFilmDelete} color="error">Yes</Button>
                                    </DialogActions>
                                </Dialog>

                                {/* Snackbar */}
                                <Snackbar
                                    open={openSnackbar}
                                    autoHideDuration={4000}
                                    onClose={handleCloseSnackbar}
                                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                                >
                                    <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                                        {snackbarMessage}
                                    </Alert>
                                </Snackbar>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default FilmList;
